/**
 * `futurex plan "<task>"` / `futurex resume [id]` — the pre-build orchestration
 * pipeline (Blueprint v2 P7 + the planning layer). Before anything is built:
 *
 *   context → goal (end goal + definition of done, or ONE clarifying question)
 *           → value (inferred project importance) + budget (effort × value)
 *           → draft (model/effort-aware plan: research, tooling areas, task DAG)
 *           → final (tooling candidates ranked + resolved, rationale recorded)
 *
 * Every stage is checkpointed under `~/.futurex/pipelines/<id>/`, so the plan
 * is inspectable (`futurex resume <id>`) and re-running resumes instead of
 * re-calling the model. The build command executes AGAINST the final plan.
 */
import { createAiClient } from '@fim/ai-sdk';
import type { EffortLevel, RepoCard } from '@fim/futurex-protocol';
import { buildRepoCard, findRepoRoot, readMemory } from './repo.js';
import { loadRetriever } from './index-store.js';
import { computeBudget } from './budget.js';
import { deriveGoal, formatGoal, type GoalResult, type SessionGoal } from './goal.js';
import {
  draftBuildPlan,
  scheduleByDeps,
  type BuildPlan,
  type BuildPlanDraft,
  type PlanResult,
  type Task,
} from './planner.js';
import { loadPlanningConfig, modelProfile, type ValueTier } from './planning-config.js';
import { rankCandidates, type SelectionResult } from './selection.js';
import { assessProjectValue, formatValue, overrideValueTier, type ValueAssessment } from './value.js';
import {
  clearCheckpoints,
  listCheckpoints,
  listPipelines,
  pipelineId,
  readCheckpoint,
  writeCheckpoint,
} from './pipeline.js';
import type { CliOptions } from './options.js';
import { color, error, info, line } from './ui.js';

export interface PlanFlags {
  /** Ignore checkpoints and re-plan from scratch. */
  readonly restart?: boolean;
  /** User override for the inferred value tier (`--value`). */
  readonly valueTier?: ValueTier;
  /** User cap on the token budget (`--budget`). */
  readonly budgetTokens?: number;
}

export type BuildPlanOutcome =
  | { readonly kind: 'plan'; readonly id: string; readonly plan: BuildPlan }
  | { readonly kind: 'clarify'; readonly question: string };

function contextSummary(root: string, card: RepoCard): string {
  const symbols = loadRetriever(root)?.symbols ?? null;
  const stack = [card.language, card.framework, card.backend, card.orm, card.packageManager]
    .filter((x): x is string => Boolean(x))
    .join(', ');
  const modules = symbols
    ? [...new Set(symbols.all().filter((d) => d.exported).map((d) => d.file))].slice(0, 30)
    : [];
  return (
    `Repo: ${card.name ?? root}\nStack: ${stack || 'unknown'}\n` +
    `Key files:\n${modules.map((m) => `- ${m}`).join('\n')}`
  );
}

/**
 * Produce (or resume) the full pre-build plan for `task`. Shared by `plan`
 * (which prints it) and `build` (which executes against it).
 */
export async function ensureBuildPlan(
  task: string,
  opts: CliOptions,
  flags: PlanFlags = {},
  onStage?: (name: string, status: 'cached' | 'run') => void,
): Promise<BuildPlanOutcome> {
  const root = findRepoRoot();
  const id = pipelineId(`${root}::${task}`);
  const cfg = loadPlanningConfig();
  const effort: EffortLevel = opts.effort ?? 'off';
  // --restart is made atomic by clearing EVERYTHING up front (plan stages AND
  // exec-* progress) — an interruption mid-restart can never leave a pipeline
  // that silently mixes old and new generations on the next resume.
  if (flags.restart) clearCheckpoints(id, '');
  // --value/--budget must take effect on a RESUMED pipeline too (it is the
  // advertised recovery from budget exhaustion), so the cheap, model-free
  // stages that embed them recompute whenever an override is present.
  const hasBudgetOverride = flags.valueTier !== undefined || flags.budgetTokens !== undefined;
  const stage = <T>(name: string, fresh: () => Promise<T> | T, force = false): Promise<T> | T => {
    if (!force) {
      const cp = readCheckpoint(id, name);
      if (cp) {
        onStage?.(name, 'cached');
        return cp.data as T;
      }
    }
    onStage?.(name, 'run');
    return fresh();
  };
  const checkpoint = <T>(name: string, data: T): T => {
    writeCheckpoint(id, name, data);
    return data;
  };

  // 1. context — repo card + key modules (cheap, local).
  const card = buildRepoCard(root);
  const context = await stage('context', () =>
    checkpoint('context', { context: contextSummary(root, card), task }),
  );

  // 2. goal — explicit end goal + definition of done. A clarify outcome is NOT
  //    checkpointed: the user answers, re-runs, and we derive again with the
  //    refined task (which keys a fresh pipeline anyway).
  const ai = createAiClient({ provider: 'deepseek' });
  const goalCp = readCheckpoint(id, 'goal');
  let goal: SessionGoal;
  if (goalCp) {
    onStage?.('goal', 'cached');
    goal = goalCp.data as SessionGoal;
  } else {
    onStage?.('goal', 'run');
    const result: GoalResult = await deriveGoal(task, context.context, ai, {
      model: opts.model,
      effort,
    });
    if (result.kind === 'clarify') return { kind: 'clarify', question: result.question };
    goal = checkpoint('goal', result.goal);
  }

  // 3. value + budget — inferred importance composed with the selected effort.
  //    Recomputed (no model call) when --value/--budget is supplied.
  const valueAndBudget = await stage(
    'value',
    () => {
      const detected = assessProjectValue(
        {
          card,
          indexedChunks: loadRetriever(root)?.size ?? 0,
          task,
          memory: readMemory(root),
        },
        cfg,
      );
      const value = flags.valueTier ? overrideValueTier(detected, flags.valueTier) : detected;
      const budget = computeBudget(effort, value.tier, cfg, {
        ...(flags.budgetTokens !== undefined ? { maxTokens: flags.budgetTokens } : {}),
      });
      return checkpoint('value', { value, budget });
    },
    hasBudgetOverride,
  );
  const { value, budget } = valueAndBudget as {
    value: ValueAssessment;
    budget: ReturnType<typeof computeBudget>;
  };

  // 4. draft — the model proposes research steps, tooling candidates, task DAG.
  let draftFresh = false;
  const draft = await stage('draft', async () => {
    draftFresh = true;
    const d = await draftBuildPlan(goal, context.context, ai, {
      model: opts.model,
      effort,
      budget,
      modelProfile: modelProfile(cfg, opts.model),
    });
    if (!d) throw new Error('the planner returned no usable plan — try rephrasing the task');
    return checkpoint('draft', d);
  });

  // 5. final — rank candidates per tooling area (popularity + maintenance +
  //    docs) and record WHY each winner was chosen. Re-assembled (no model
  //    call) when the embedded value/budget changed.
  const plan = await stage(
    'final',
    async () => {
      // A freshly-drafted task DAG invalidates per-task execution progress; a
      //  mere budget/value recompute must NOT (that is the "continue with a
      //  bigger budget" flow).
      if (draftFresh) clearCheckpoints(id, 'exec-');
      const tooling: SelectionResult[] = [];
      for (const area of (draft as BuildPlanDraft).tooling) {
        const resolved = await rankCandidates(area.area, area.candidates, cfg);
        if (resolved) tooling.push(resolved);
      }
      const final: BuildPlan = {
        goal,
        model: opts.model,
        ...(opts.tier ? { tier: opts.tier } : {}),
        effort,
        value,
        budget,
        research: (draft as BuildPlanDraft).research,
        tooling,
        packages: tooling.map((t) => t.chosen),
        tasks: (draft as BuildPlanDraft).tasks,
      };
      return checkpoint('final', final);
    },
    hasBudgetOverride,
  );

  return { kind: 'plan', id, plan: plan as BuildPlan };
}

function printTaskDag(tasks: readonly Task[]): void {
  const batches = scheduleByDeps(tasks);
  line(`build: ${tasks.length} tasks in ${batches.length} stage${batches.length === 1 ? '' : 's'}`);
  batches.forEach((batch, i) => {
    line(color.bold(`  stage ${i + 1}${batch.length > 1 ? ' (parallel)' : ''}:`));
    for (const t of batch) {
      const deps = t.dependsOn.length ? ` ${color.dim(`[after ${t.dependsOn.join(', ')}]`)}` : '';
      line(`    ${color.cyan(t.id)} ${t.title}${deps}`);
      if (t.targets.length) line(color.dim(`        targets: ${t.targets.join(', ')}`));
    }
  });
}

/** Print the full pre-build plan (goal, value, budget, research, tooling, DAG). */
export function printBuildPlan(plan: BuildPlan): void {
  const cfg = loadPlanningConfig();
  for (const l of formatGoal(plan.goal)) line(l);
  line(formatValue(plan.value, cfg));
  line(`budget: ${plan.budget.explanation}`);
  line(
    color.dim(
      `depth: ${plan.budget.researchSteps} research · ${plan.budget.candidatesPerChoice} candidates/choice · ` +
        `${plan.budget.validationPasses} validation pass${plan.budget.validationPasses === 1 ? '' : 'es'} · ` +
        // Never surface the underlying provider model id — show the effort only.
        `effort ${plan.effort}`,
    ),
  );
  if (plan.research.length > 0) {
    line(color.bold('research first:'));
    for (const r of plan.research) line(`  ${r.id}. ${r.question}`);
  }
  if (plan.tooling.length > 0) {
    line(color.bold('tooling (decided before building):'));
    for (const t of plan.tooling) {
      line(`  ${t.area}: ${color.cyan(t.chosen)}`);
      line(color.dim(`    why: ${t.rationale}`));
    }
  }
  printTaskDag(plan.tasks);
}

/** `futurex plan "<task>"` — produce + print the pre-build plan (resumable). */
export async function runPlan(task: string, opts: CliOptions, flags: PlanFlags = {}): Promise<void> {
  const q = task.trim();
  if (!q) {
    error('usage: futurex plan "<task>"');
    process.exitCode = 1;
    return;
  }
  const id = pipelineId(`${findRepoRoot()}::${q}`);
  info(`pipeline ${id}${flags.restart ? ' (restart)' : ''}`);
  try {
    const outcome = await ensureBuildPlan(q, opts, flags, (name, status) =>
      info(`  ${status === 'cached' ? '✓ resumed' : '… running'} ${name}`),
    );
    if (outcome.kind === 'clarify') {
      info('one question before planning:');
      line(`  ? ${outcome.question}`);
      line(color.dim('(answer by re-running plan with the detail added)'));
      return;
    }
    printBuildPlan(outcome.plan);
    line(color.dim(`(build it with \`futurex build "${q}"\`, or inspect via \`futurex resume ${outcome.id}\`)`));
  } catch (err) {
    error(err instanceof Error ? err.message : String(err));
    process.exitCode = 1;
  }
}

/** `futurex resume [id]` — inspect pipeline checkpoints (list all, or show one's plan). */
export function runResume(id?: string): void {
  if (!id) {
    const ids = listPipelines();
    if (ids.length === 0) {
      info('no pipelines yet — run `futurex plan "<task>"`');
      return;
    }
    info(`pipelines (${ids.length}):`);
    for (const pid of ids) line(`  ${pid} — ${listCheckpoints(pid).join(', ') || 'no checkpoints'}`);
    line(color.dim('show one with `futurex resume <id>`'));
    return;
  }
  const stages = listCheckpoints(id);
  if (stages.length === 0) {
    info(`no checkpoints for pipeline ${id}`);
    return;
  }
  info(`pipeline ${id}: checkpoints = ${stages.join(', ')}`);
  const finalCp = readCheckpoint(id, 'final');
  if (finalCp) {
    printBuildPlan(finalCp.data as BuildPlan);
    return;
  }
  // Legacy pipelines (pre-planning-layer) checkpointed a bare task DAG as 'plan'.
  const legacy = readCheckpoint(id, 'plan');
  if (legacy) {
    const result = legacy.data as PlanResult;
    if (result.kind === 'plan') printTaskDag(result.plan.tasks);
    else for (const q of result.questions) line(`  ? ${q}`);
    return;
  }
  line(color.dim('plan stage not reached — re-run `futurex plan "<task>"` to continue'));
}
